import React, { forwardRef, useEffect, useMemo, useState } from "react";
import { RESUME_ITEMS, best, contact, withTopHit } from "../../data/search";
import { useKeyboardInset } from "./ios";
import notesIcon from "../../assets/icons/ios/notes.png";
import mailIcon from "../../assets/icons/ios/mail.png";
import safariIcon from "../../assets/icons/ios/safari.png";
import { ReactComponent as SearchIcon } from "../../assets/svgs/sf-symbols/notes/magnifyingglass.svg";
import { ReactComponent as FullScreenGlyph } from "../../assets/svgs/sf-symbols/ios/arrow.up.left.and.arrow.down.right.svg";
import { ReactComponent as CloseGlyph } from "../../assets/svgs/sf-symbols/ios/xmark.svg";

const openUrl = (href) => window.open(href, "_blank", "noopener");

const ItemIcon = ({ item, large }) =>
  item.icon ? (
    <img src={item.icon} alt="" className={`ios-sr-icon ${large ? "ios-sr-icon--large" : ""}`} draggable={false} />
  ) : (
    <span className={`ios-sr-icon ios-icon--fullscreen ${large ? "ios-sr-icon--large" : ""}`}>
      <FullScreenGlyph className="ios-sr-glyph" />
    </span>
  );

// iOS 26 Search, opened from the Home Screen's Search button or by swiping down on it: the
// wallpaper blurs, the field waits at the bottom (above the keyboard), suggestions show until
// you type, then results: a Top Hit, apps, places in the resume, and ways to get in touch.
// `homeApps` are the Home Screen's apps, each doing what tapping its icon does.
// It stays mounted while closed so the field can take focus during the opening tap, which is
// what makes iOS Safari show the keyboard.
const MobileSearch = forwardRef(({ open, onClose, onOpenResume, homeApps }, inputRef) => {
  const [query, setQuery] = useState("");
  const keyboard = useKeyboardInset(open);

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const email = contact("Email");
  const website = contact("Website");
  const linkedin = contact("LinkedIn");

  const apps = useMemo(
    () => [{ id: "app-notes", title: "Notes", keywords: "resume cv", icon: notesIcon, run: () => onOpenResume() }, ...homeApps],
    [homeApps, onOpenResume]
  );

  const notes = useMemo(
    () => [
      {
        id: "doc-resume",
        title: "Resume",
        subtitle: "Sylvester Asare Sarpong",
        keywords: "cv sylvester",
        icon: notesIcon,
        run: () => onOpenResume(),
      },
      ...RESUME_ITEMS.map((item) => ({ ...item, icon: notesIcon, run: () => onOpenResume(item.anchor) })),
    ],
    [onOpenResume]
  );

  const actions = useMemo(
    () => [
      {
        id: "act-email",
        title: "Email Sylvester",
        subtitle: email.text,
        keywords: "contact mail hire message",
        icon: mailIcon,
        run: () => openUrl(email.href),
      },
      {
        id: "act-linkedin",
        title: "View LinkedIn Profile",
        subtitle: linkedin.text,
        keywords: "contact social",
        icon: safariIcon,
        run: () => openUrl(linkedin.href),
      },
      {
        id: "act-website",
        title: `Visit ${website.text.replace(/^www\./, "")}`,
        keywords: "website site portfolio",
        icon: safariIcon,
        run: () => openUrl(website.href),
      },
    ],
    [email, linkedin, website]
  );

  const q = query.trim().toLowerCase();
  const sections = useMemo(() => {
    if (!q) return [];
    return withTopHit(
      [
        { title: "Apps", items: best(apps, q, 4), grid: true },
        { title: "Notes", items: best(notes, q, 6) },
        { title: "Actions", items: best(actions, q, 3) },
      ],
      q
    );
  }, [q, apps, notes, actions]);
  const first = sections.length ? sections[0].items[0] : null;

  const run = (item) => {
    if (inputRef.current) inputRef.current.blur();
    onClose();
    item.run();
  };

  // Before typing: the resume and the ways to get in touch.
  const suggestions = [notes[0], ...actions];

  return (
    <div
      className={`ios-search-layer ${open ? "is-open" : ""}`}
      style={{ "--keyboard": `${keyboard}px` }}
      aria-hidden={!open}
      role="dialog"
      aria-label="Search"
    >
      <div className="ios-sr-scroll" onClick={(e) => e.target === e.currentTarget && inputRef.current.blur()}>
        {!q && (
          <section>
            <h2 className="ios-sr-title">Siri Suggestions</h2>
            <div className="ios-sr-platter">
              {suggestions.map((item) => (
                <button type="button" key={item.id} className="ios-sr-row" onClick={() => run(item)} tabIndex={open ? 0 : -1}>
                  <ItemIcon item={item} />
                  <span className="ios-sr-text">
                    <span className="ios-sr-row-title">{item.title}</span>
                    {item.subtitle && <span className="ios-sr-row-subtitle">{item.subtitle}</span>}
                  </span>
                </button>
              ))}
            </div>
          </section>
        )}

        {sections.map((section) => (
          <section key={section.title}>
            <h2 className="ios-sr-title">{section.title}</h2>
            {section.grid ? (
              <div className="ios-sr-grid">
                {section.items.map((item) => (
                  <button type="button" key={item.id} className="ios-sr-app" onClick={() => run(item)}>
                    <ItemIcon item={item} />
                    <span>{item.title}</span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="ios-sr-platter">
                {section.items.map((item) => (
                  <button
                    type="button"
                    key={item.id}
                    className={`ios-sr-row ${section.top ? "ios-sr-row--top" : ""}`}
                    onClick={() => run(item)}
                  >
                    <ItemIcon item={item} large={section.top} />
                    <span className="ios-sr-text">
                      <span className="ios-sr-row-title">{item.title}</span>
                      {item.subtitle && <span className="ios-sr-row-subtitle">{item.subtitle}</span>}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </section>
        ))}

        {q && !sections.length && <p className="ios-sr-empty">No Results</p>}
      </div>

      <form
        className="ios-sr-bar"
        onSubmit={(e) => {
          e.preventDefault();
          if (first) run(first);
        }}
      >
        <label className="ios-sr-field glass glass--clear">
          <SearchIcon className="ios-sr-field-icon" aria-hidden="true" />
          <input
            ref={inputRef}
            type="search"
            enterKeyHint="go"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Escape" && onClose()}
            placeholder="Search"
            aria-label="Search"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            tabIndex={open ? 0 : -1}
          />
        </label>
        <button
          type="button"
          className="ios-sr-close glass glass--clear"
          onClick={() => {
            inputRef.current.blur();
            onClose();
          }}
          aria-label="Close Search"
          tabIndex={open ? 0 : -1}
        >
          <CloseGlyph className="ios-sr-close-glyph" aria-hidden="true" />
        </button>
      </form>
    </div>
  );
});

export default MobileSearch;
